import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UiTextProvider } from '@uplink/ui';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { setErrorMessages, useTRPC } from './api.ts';
import { en } from './i18n/en.ts';
import { type Messages, ru } from './i18n/ru.ts';
import { getThemePreference, inTelegram, setThemePreference, type ThemePref } from './telegram.ts';

export type Lang = 'ru' | 'en';
export type Currency = 'USD' | 'EUR' | 'RUB';
export const LANG_NAMES: Record<Lang, string> = { ru: 'Русский', en: 'English' };
export const CURRENCY_SIGNS: Record<Currency, string> = { USD: '$', EUR: '€', RUB: '₽' };

const DICTS: Record<Lang, Messages> = { ru, en };
const STORE_KEY = 'uplink.prefs';

/** Last known choice on this device, so the first paint is already in the right language. */
function remembered(): { lang: Lang; currency: Currency } {
  let saved: Partial<{ lang: Lang; currency: Currency }> = {};
  try {
    saved = JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}');
  } catch {}
  // Сайт по умолчанию открывается на русском, а Mini App учитывает язык устройства.
  const guess: Lang = !inTelegram || /^(ru|uk|be|kk)\b/.test(navigator.language) ? 'ru' : 'en';
  return {
    lang: saved.lang === 'ru' || saved.lang === 'en' ? saved.lang : guess,
    currency: saved.currency && saved.currency in CURRENCY_SIGNS ? saved.currency : 'RUB',
  };
}

/**
 * USD cents → text in the display currency. Dollars and euros keep cents; rubles are rounded to whole
 * rubles. Money is kept in dollars, so other currencies are a view at today's rate.
 */
export function formatMoney(cents: number, currency: Currency, rates: Record<Currency, number> | null) {
  const cur = currency !== 'USD' && rates?.[currency] ? currency : 'USD';
  const value = (Math.abs(cents) / 100) * (cur === 'USD' ? 1 : rates![cur]);
  const sign = cents < 0 ? '−' : '';
  if (cur === 'RUB') {
    const whole = Math.round(value).toLocaleString('ru-RU');
    return `${sign}${whole} ₽`;
  }
  const fixed = Math.round(value * 100) / 100;
  const [int, frac] = fixed.toFixed(2).split('.');
  return `${sign}${CURRENCY_SIGNS[cur]}${Number(int).toLocaleString('en-US')}.${frac}`;
}

interface Prefs {
  lang: Lang;
  currency: Currency;
  theme: ThemePref;
  /** False until the exchange rates arrive; prices fall back to dollars meanwhile. */
  ratesReady: boolean;
  t: Messages;
  money: (cents: number) => string;
  /** Always dollars: what is actually charged or credited. */
  usd: (cents: number) => string;
  date: (iso: string) => string;
  dateTime: (iso: string) => string;
  countryName: (code: string) => string;
  setLang: (lang: Lang) => void;
  setCurrency: (currency: Currency) => void;
  setTheme: (theme: ThemePref) => void;
}

const Ctx = createContext<Prefs | null>(null);

export function usePrefs(): Prefs {
  const prefs = useContext(Ctx);
  if (!prefs) throw new Error('usePrefs outside PrefsProvider');
  return prefs;
}

export function PrefsProvider({ children }: { children: ReactNode }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const me = useQuery(trpc.me.queryOptions());
  const config = useQuery(trpc.config.queryOptions());
  const [local, setLocal] = useState(remembered);
  const [theme, setThemeState] = useState<ThemePref>(getThemePreference);

  // The server copy wins once it arrives (it is shared with the bot)
  useEffect(() => {
    if (!me.data) return;
    setLocal({ lang: me.data.lang, currency: me.data.currency });
  }, [me.data]);
  useEffect(() => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(local));
    } catch {}
    document.documentElement.lang = local.lang;
  }, [local]);

  const save = useMutation(
    trpc.settings.update.mutationOptions({
      onSuccess: (saved) => {
        queryClient.setQueryData(trpc.me.queryKey(), (old) => (old ? { ...old, ...saved } : old));
        // Texts the server writes (history titles, errors) follow the language
        queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0]?.toString() !== 'me' });
      },
    }),
  );

  const value = useMemo<Prefs>(() => {
    const t = DICTS[local.lang];
    const rates = config.data?.rates ?? null;
    const dateFmt = new Intl.DateTimeFormat(t.locale, { day: 'numeric', month: 'long', year: 'numeric' });
    const dateTimeFmt = new Intl.DateTimeFormat(t.locale, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
    const regions = new Intl.DisplayNames([t.locale], { type: 'region' });
    return {
      lang: local.lang,
      currency: local.currency,
      theme,
      ratesReady: !!rates,
      t,
      money: (cents) => formatMoney(cents, local.currency, rates),
      usd: (cents) => formatMoney(cents, 'USD', null),
      // "14 ноября 2027 г." without the trailing "г."
      date: (iso) => dateFmt.format(new Date(iso)).replace(/\s?г\.$/, ''),
      dateTime: (iso) => dateTimeFmt.format(new Date(iso)),
      countryName: (code) => {
        try {
          return regions.of(code) ?? code;
        } catch {
          return code;
        }
      },
      setLang: (lang) => {
        setLocal((p) => ({ ...p, lang }));
        save.mutate({ lang });
      },
      setCurrency: (currency) => {
        setLocal((p) => ({ ...p, currency }));
        save.mutate({ currency });
      },
      setTheme: (next) => {
        setThemePreference(next);
        setThemeState(next);
      },
    };
  }, [local, theme, config.data?.rates, save.mutate]);

  // errorText() is called outside React components too
  setErrorMessages(value.t.errors);

  return (
    <Ctx.Provider value={value}>
      <UiTextProvider value={value.t.ui}>{children}</UiTextProvider>
    </Ctx.Provider>
  );
}
